/**
 * Evolução da conta mês a mês — saldo inicial, receitas, despesas e saldo final.
 */
import { useMemo, useState } from 'react'
import { formatCurrency } from '../../../../utils/obraAnalytics'

const CHART = { width: 640, height: 200, padX: 48, padY: 28 }

const EvolucaoContaMensal = ({ title = 'Evolução da conta', serie = [], mesDestaque = null }) => {
    const [hover, setHover] = useState(null)

    const pontos = useMemo(() => (serie || []).map((r) => ({
        mes_ano: r.mes_ano,
        label: r.label_pt || r.label || r.mes_ano,
        saldo_inicial: Number(r.saldo_inicial || 0),
        receitas: Number(r.receitas || 0),
        despesas: Number(r.despesas || 0),
        impostos: Number(r.impostos || 0),
        saldo_final: Number(r.saldo_final || 0),
        movimento_liquido: Number(r.resultado ?? r.movimento_liquido ?? r.saldo ?? 0),
    })), [serie])

    const layout = useMemo(() => {
        if (!pontos.length) return null
        const valores = pontos.flatMap((p) => [p.saldo_inicial, p.saldo_final])
        const min = Math.min(...valores, 0)
        const max = Math.max(...valores, 0)
        const span = max - min || 1
        const innerW = CHART.width - CHART.padX * 2
        const innerH = CHART.height - CHART.padY * 2
        const step = pontos.length > 1 ? innerW / (pontos.length - 1) : 0
        const toY = (v) => CHART.padY + innerH - ((v - min) / span) * innerH

        const pts = pontos.map((p, idx) => ({
            ...p,
            x: CHART.padX + idx * step,
            y: toY(p.saldo_final),
        }))

        return {
            pts,
            line: pts.map((p) => `${p.x},${p.y}`).join(' '),
            zeroY: toY(0),
        }
    }, [pontos])

    if (!pontos.length) {
        return (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                <p className="text-xs text-gray-400 mt-2">Sem meses no período.</p>
            </div>
        )
    }

    const active = hover != null ? layout.pts[hover] : null
    const destaqueFixo = !active && mesDestaque
        ? pontos.find((p) => p.mes_ano === mesDestaque)
        : null
    const painel = active || destaqueFixo

    return (
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2 min-h-[88px]">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                </div>
                <div className="text-right text-xs space-y-0.5 min-w-[160px] min-h-[72px]">
                    {painel ? (
                        <>
                            <p className="font-bold text-gray-800">{painel.label}</p>
                            <p className="text-gray-600">Inicial {formatCurrency(painel.saldo_inicial)}</p>
                            <p className="text-emerald-700">+ {formatCurrency(painel.receitas)}</p>
                            <p className="text-rose-700">− {formatCurrency(painel.despesas)}</p>
                            <p className="text-amber-700">− {formatCurrency(painel.impostos)} (impostos)</p>
                            <p className={`font-bold ${painel.saldo_final >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                                Final {formatCurrency(painel.saldo_final)}
                            </p>
                        </>
                    ) : (
                        <p className="text-gray-400 pt-4">Passe o mouse sobre um mês</p>
                    )}
                </div>
            </div>

            {layout && (
                <div className="overflow-x-auto">
                    <svg width={CHART.width} height={CHART.height} viewBox={`0 0 ${CHART.width} ${CHART.height}`} className="mx-auto min-w-[320px]">
                        <line x1={CHART.padX} y1={layout.zeroY} x2={CHART.width - CHART.padX} y2={layout.zeroY} stroke="#e5e7eb" strokeWidth="1" />
                        <polyline fill="none" stroke="#be123c" strokeWidth="2.5" points={layout.line} strokeLinecap="round" strokeLinejoin="round" />
                        {layout.pts.map((p, idx) => {
                            const destaque = mesDestaque && p.mes_ano === mesDestaque
                            return (
                                <g key={p.mes_ano}>
                                    <circle cx={p.x} cy={p.y} r="12" fill="transparent" className="cursor-pointer" onMouseEnter={() => setHover(idx)} onMouseLeave={() => setHover(null)} />
                                    <circle cx={p.x} cy={p.y} r={hover === idx || destaque ? 5 : 3} fill={destaque ? '#f59e0b' : '#be123c'} className="pointer-events-none" />
                                    <text x={p.x} y={CHART.height - 8} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 9 }}>
                                        {p.label}
                                    </text>
                                </g>
                            )
                        })}
                    </svg>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100">
                            <th className="py-2 pr-3 font-semibold">Mês</th>
                            <th className="py-2 pr-3 font-semibold text-right">Saldo inicial</th>
                            <th className="py-2 pr-3 font-semibold text-right text-emerald-700">Receitas</th>
                            <th className="py-2 pr-3 font-semibold text-right text-rose-700">Despesas</th>
                            <th className="py-2 pr-3 font-semibold text-right text-amber-700">Impostos</th>
                            <th className="py-2 pr-3 font-semibold text-right">Resultado</th>
                            <th className="py-2 font-semibold text-right">Saldo final</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pontos.map((p) => {
                            const destaque = mesDestaque && p.mes_ano === mesDestaque
                            return (
                                <tr
                                    key={p.mes_ano}
                                    className={`border-b border-gray-50 last:border-0 ${destaque ? 'bg-amber-50/60' : ''}`}
                                >
                                    <td className="py-2 pr-3 font-semibold text-gray-800">{p.label}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{formatCurrency(p.saldo_inicial)}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">{formatCurrency(p.receitas)}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums text-rose-700">{formatCurrency(p.despesas)}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums text-amber-700">{formatCurrency(p.impostos)}</td>
                                    <td className={`py-2 pr-3 text-right tabular-nums font-medium ${p.movimento_liquido >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                                        {formatCurrency(p.movimento_liquido)}
                                    </td>
                                    <td className="py-2 text-right tabular-nums font-bold text-gray-900">{formatCurrency(p.saldo_final)}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default EvolucaoContaMensal
